import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { ReactNode } from "react"

/** The "+N" chip that stands in for items a row has no width for; hovering it
 *  shows the hidden ones as chips. Shared so the inbox's framework column and
 *  the editor's refusal panel overflow the same way — a reader who learns the
 *  gesture in one place keeps it in the other. */
export function OverflowChip({
  count,
  testId,
  children,
  interactive = false,
}: {
  count: number
  testId?: string
  children: ReactNode
  /** The hidden items are themselves controls (the refusal panel's product
   *  buttons). A tooltip's content is never in the tab order, so those would be
   *  unreachable by keyboard — use a popover, which traps focus (review 3, F7). */
  interactive?: boolean
}) {
  const trigger = (
    <button
      type="button"
      data-testid={testId}
      aria-label={`+${count}`}
      className="font-mono inline-flex h-[20px] shrink-0 items-center rounded-full border border-grey4 bg-transparent px-1.5 text-[10.5px] text-grey5 hover:text-white focus-visible:outline-solid focus-visible:outline-1 focus-visible:outline-grey5"
    >
      +{count}
    </button>
  )
  const panel =
    "flex max-h-64 max-w-xs flex-wrap gap-1 overflow-y-auto border border-grey4 bg-grey2 p-2"

  if (interactive) {
    return (
      <Popover>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent side="bottom" className={panel}>
          {children}
        </PopoverContent>
      </Popover>
    )
  }
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side="bottom" className={panel}>
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
